import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { apiGet, apiPost } from '../../../lib/shopperApi';

/**
 * Message thread for a single order. Mounted inside OrderHistory when a
 * row is expanded.
 *
 *   - Fetches /auth/orders/{id}/messages on mount
 *   - Renders a chronological thread (customer right-aligned, merchant left)
 *   - Textarea + Send button posts to the same path
 *   - Auto-scrolls to bottom on new message
 *
 * Backend filters out merchant-internal notes (is_customer_visible=0) so
 * the shopper only sees their own messages + merchant replies meant for them.
 */
const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.includes('T') ? '' : 'Z'));
  if (isNaN(d.getTime())) return iso;
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const OrderMessageThread = ({ orderId }) => {
  const [messages, setMessages] = useState(null); // null = loading
  const [body, setBody]         = useState('');
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);
  const [flash, setFlash]       = useState(null);
  const endRef = useRef(null);

  const load = async () => {
    const { ok, data } = await apiGet(`/orders/${orderId}/messages`);
    if (!ok) { setErr(data?.error || 'Could not load messages.'); setMessages([]); return; }
    setMessages(Array.isArray(data?.messages) ? data.messages : []);
    setErr(null);
  };

  useEffect(() => { load(); }, [orderId]);

  useEffect(() => {
    if (messages && messages.length > 0 && endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages?.length]);

  const send = async () => {
    const trimmed = body.trim();
    if (trimmed.length < 2) { setErr('Message is too short.'); return; }
    setBusy(true); setErr(null); setFlash(null);
    const { ok, data } = await apiPost(`/orders/${orderId}/messages`, { body: trimmed });
    setBusy(false);
    if (!ok) { setErr(data?.error || 'Could not send message.'); return; }
    setMessages((prev) => [...(prev || []), data.message]);
    setBody('');
    setFlash('Sent. We\'ll reply by email and here.');
    setTimeout(() => setFlash(null), 4000);
  };

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="flex items-center mb-3">
        <Icon name="MessageSquare" size={16} className="text-muted-foreground mr-2" />
        <h4 className="text-sm font-semibold text-foreground">Messages on this order</h4>
      </div>

      {messages === null ? (
        <p className="text-xs text-muted-foreground">Loading messages…</p>
      ) : messages.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No messages yet. Send one below if you need help with this order.
        </p>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {messages.map((m) => {
            const mine = m.author_type === 'customer';
            return (
              <li key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    mine
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-accent/30 text-foreground rounded-bl-sm'
                  }`}
                >
                  <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                  <div className={`text-[10px] mt-1 ${mine ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {mine ? 'You' : 'Seller'} · {fmtWhen(m.created_at)}
                  </div>
                </div>
              </li>
            );
          })}
          <li ref={endRef}></li>
        </ul>
      )}

      {err && <p className="text-xs text-red-700 mt-2">{err}</p>}
      {flash && <p className="text-xs text-green-700 mt-2">{flash}</p>}

      <div className="mt-3 flex gap-2 items-end">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type your message…"
          rows={2}
          maxLength={5000}
          className="flex-1 px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-ring/30 focus:border-ring resize-none"
          disabled={busy}
        />
        <Button onClick={send} disabled={busy || body.trim().length < 2} iconName="Send" iconPosition="left" size="sm">
          {busy ? 'Sending…' : 'Send'}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">
        The seller is notified by email; replies appear here.
      </p>
    </div>
  );
};

export default OrderMessageThread;
